import {
  Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import { CampoUpdate } from 'src/app/models/campo-model-update';
import { Campo } from 'src/app/models/coleta';
import { DocumentTypeMapping, EnvConfig } from 'src/app/models/env-config';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { ThemeService } from 'src/app/services/theme.service';
import { environment } from 'src/environments/environment';
import {
  DocumentCameraType,
  DocumentCameraTypes,
  ErrorPictureResponse,
  SDKEnvironmentTypes,
  SelfieCameraTypes,
  SuccessPictureResponse,
  SupportPictureResponse,
  UnicoCheckBuilder,
  UnicoConfig,
  UnicoThemeBuilder,
} from "unico-webframe";

type UnicoCredentials = {
  project_info: {
    project_number: string,
    project_id: string
  },
  client_info: {
    mobilesdk_app_id: string,
    js_client_info: {
      hostname: string
    }
  },
  host_info: {
    host_info: string
    host_key: string
  }
}

@Component({
  selector: 'app-image-capture-unico',
  templateUrl: './image-capture-unico.component.html',
  styleUrls: ['./image-capture-unico.component.scss'],
})
export class ImageCaptureUnicoComponent implements OnInit {

  @Output()
  imageCapture = new EventEmitter<{ image: string; appKey: string }>();

  @Output()
  captureError = new EventEmitter<string>();

  @Output()
  patchFormValue = new EventEmitter<CampoUpdate>();


  @Input() isSelf: Boolean = false;

  @Input() exibirTelaCaptura: Boolean = false;

  @Input() campo: Campo = {};

  @Output()
  isLoading = new EventEmitter<Boolean>();

  constructor(private themeService: ThemeService, private sessionStorageService: SessionStorageService) {
  }


  ngOnInit(): void {
    const urlPathModels = this.getHostUrlBase("unico/models"); //environment.production ? this.getFileUrlBase("models") : "http://localhost:4200/unico/models";
    const credentialsUrl = environment.production ? this.getFileUrlBase("unico.json") : "/unico/unico.json"; //"/unico/unico.json";
    const resourcesUrl = "/unico/resources";//environment.production ? this.getFileUrlBase("resources") : "/unico/resources";

    const imageCapture_ = this.imageCapture;
    const patchFormValue_ = this.patchFormValue;
    const captureError_ = this.captureError;
    const isLoading_ = this.isLoading;
    const isSelf_ = this.isSelf;
    const campo_ = this.campo;
    const envConfig_ = this.sessionStorageService.getEnvConfig();
    const unicoConfig = envConfig_.unico;

    const callback = {
      on: {
        success: function (obj: SuccessPictureResponse) {
          //A seguinte regex remove os metadatas que vem antes do base64 Ex: data:image/jpeg;base64;
          const encodedBase64Image = obj.base64.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');

          imageCapture_.emit({ image: encodedBase64Image, appKey: '' });
          patchFormValue_.emit({ campo: campo_.codigo ? campo_.codigo : "", valor: obj.encrypted });
        },
        error: function (error: ErrorPictureResponse) {
          window.console.log(error);

          const mensagem = `Motivo: ${error.code} - ${error.message}`;

          captureError_.emit(mensagem);
        },
        support: function (error: SupportPictureResponse) {
          window.console.log(error);

          const mensagem = `iOS: Safari
          Android/Windows: Chrome, Firefox`;

          captureError_.emit(mensagem);
        },
      },
    };

    const { colorSecondary } = this.themeService.getColorTheme();

    //Configurando o tema smilego.
    const unicoTheme = new UnicoThemeBuilder()
      .setColorSilhouetteSuccess(colorSecondary)
      .setColorSilhouetteError("#ed5565")
      .setColorSilhouetteNeutral("#fcfcfc")
      .setBackgroundColor("#f3f3f4")
      .setColorText(colorSecondary)
      .setBackgroundColorComponents(colorSecondary)
      .setColorTextComponents("#f3f3f4")
      .setBackgroundColorButtons(colorSecondary)
      .setColorTextButtons("#f3f3f4")
      .setBackgroundColorBoxMessage("#fff")
      .setColorTextBoxMessage("#000")
      .setHtmlPopupLoading(
        `<div style="position: absolute; top: 45%; right: 50%; transform:
    translate(50%, -50%); z-index: 10; text-align: center;">Carregando...</div>`
      )
      .build();

    // Inicializa o builder do SDK.
    const unicoCameraBuilder = new UnicoCheckBuilder();
    unicoCameraBuilder.setTheme(unicoTheme);
    unicoCameraBuilder.setModelsPath(urlPathModels);
    unicoCameraBuilder.setResourceDirectory(resourcesUrl);
    if (unicoConfig?.hostKey) {
      console.log('setando environment da SDK')
      unicoCameraBuilder.setEnvironment(unicoConfig.prodEnabled ? SDKEnvironmentTypes.PROD : SDKEnvironmentTypes.UAT);
    }

    const unicoCamera = unicoCameraBuilder.build();

    //Inicializa a câmera.
    inicializaCamera();

    //local functions 

    //Funcao responsavel por fazer o de-para para o enum do SDK.
    function selecionaTipoDocumento(campo: Campo, envConfig: EnvConfig) {

      if (envConfig
        && envConfig.mappingCampoParaUnicoCameraType
      ) {
        const mapping: DocumentTypeMapping | undefined = envConfig.mappingCampoParaUnicoCameraType.find(mapping => mapping[campo.codigo ?? ""])

        if (mapping) {
          const tipoDocumento: string = Object.values(mapping)[0];

          const documentType = DocumentCameraTypes[tipoDocumento as keyof typeof DocumentCameraTypes] as DocumentCameraType
            || DocumentCameraTypes.OTHERS(campo.titulo ? campo.titulo : "OTHER");
          return documentType;
        }

      }

      switch (campo.codigo) {
        case 'pessoa.documento.cnh.aberta': return DocumentCameraTypes.CNH;
        case 'pessoa.documento.cnh.frente': return DocumentCameraTypes.CNH_FRENTE;
        case 'pessoa.documento.cnh.verso': return DocumentCameraTypes.CNH_VERSO;
        case 'pessoa.documento.rg.frente': return DocumentCameraTypes.RG_FRENTE;
        case 'pessoa.documento.rg.verso': return DocumentCameraTypes.RG_VERSO;
        default: return DocumentCameraTypes.OTHERS(campo.titulo ? campo.titulo : "OTHER");
      }
    }

    function inicializaUnicoNovaAbordagem() {
      try {
        console.log('usando nova abordagem')
        const config = new UnicoConfig().setHostname(window.location.hostname).setHostKey(unicoConfig?.hostKey ?? '');
        console.log(config)
        if (isSelf_) {
          unicoCamera.prepareSelfieCamera(config, SelfieCameraTypes.SMART)
            .then(cameraOpener => {
              cameraOpener.open(callback);
              isLoading_.emit(true);
            });
        } else {
          unicoCamera.prepareDocumentCamera(config, selecionaTipoDocumento(campo_, envConfig_))
            .then(cameraOpener => cameraOpener.open(callback));
        }
      } catch (e) {
        isLoading_.emit(false);
        console.error(e)
        captureError_.emit(`Houve um erro ao inicializar SDK Unico para validar ${isSelf_ ? 'o rosto' : 'o documento'}`);
      }
    }

    //Metodo responsavel por inicializar a camera da unico, podendo ser selfie ou documento dependendo do boolean isSelf passado ia prop.
    function inicializaCamera() {
      console.log(envConfig_.unico)
      if (envConfig_.unico?.hostKey) {
        return inicializaUnicoNovaAbordagem();
      }

      console.log('usando metodo antigo URL')
      if (isSelf_) {
        unicoCamera.prepareSelfieCamera(credentialsUrl, SelfieCameraTypes.SMART)
          .then(cameraOpener => {
            cameraOpener.open(callback);
            isLoading_.emit(true);
          });
      } else {
        unicoCamera.prepareDocumentCamera(credentialsUrl, selecionaTipoDocumento(campo_, envConfig_))
          .then(cameraOpener => cameraOpener.open(callback));
      }
    }

  }

  getHostUrlBase(path: string) {
    return (
      window.location.protocol + "//" + window.location.host + "/" + path
    );
  }

  getFileUrlBase(path: string) {

    const emissor = this.themeService.getCurrentIssuer();
    const assetsUrl = `/assets/temas/${emissor}`

    return (
      `${assetsUrl}/${path}`
    );
  }

}
